import { useEffect, useState } from 'react'
import { adminAPI } from '../services/api'
import { apiErrorMessage } from '../utils/apiError'
import { formatDeciderTime } from './trackRecord/Phase2DeciderNote'
import type { Phase2Trial, Phase2TrialDisagreements, Phase2TrialSide } from '../types/phase2Trial'

/**
 * Settings → Engine, admins only, at the top of Phase 2 vs Phase 1: the
 * Phase 2 shadow trial (backend services/phase2_trial). Phase 1 decides what
 * publishes; Phase 2's verdict is scored beside it. Gross R, before fees.
 * Rendered as the server sends it; at 20 the server's frozen result shows,
 * and nothing switches automatically.
 */

export const TRIAL_SUBTITLE = 'Phase 1 decides what publishes · Phase 2 scored beside it · gross R, before fees'
export const WAITING_TEXT = 'No Phase 2 model has deployed yet. The trial starts when one does.'
export const NO_SWITCH_TEXT = 'Nothing switches at 20: the result is frozen and reported, and going live is a human decision.'

const signedR = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}R`
const day = (iso: string | null | undefined) =>
  iso ? (formatDeciderTime(iso) ?? iso).replace(/ \d{2}:\d{2} UTC$/, '') : '—'

/**
 * Where the verdicts split -- where the decision is actually made. Reads even
 * while Phase 2 keeps nothing and the two sides have nothing to compare.
 * Null when they have not disagreed yet.
 */
export const disagreementText = (d: Phase2TrialDisagreements | null | undefined): string | null => {
  if (!d || d.switch_gross_r === null) return null
  const part = (s: Phase2TrialSide) =>
    `${s.n} setup${s.n === 1 ? '' : 's'}${
      s.n ? ` (${signedR(s.total_gross_r ?? null)} total${s.bets ? `, ${s.bets.n} bet${s.bets.n === 1 ? '' : 's'}` : ''})` : ''
    }`
  return (
    `Phase 1 published ${part(d.phase1_only)} Phase 2 would have blocked; ` +
    `Phase 2 would have published ${part(d.phase2_only)} Phase 1 blocked. ` +
    `Letting Phase 2 decide these would have changed the total by ${signedR(d.switch_gross_r)}.`
  )
}

/** "Phase 2 ahead by +0.42R" / "Phase 2 behind by 0.30R"; null when there is nothing to compare. */
export const verdictText = (diff: number | null, ahead: boolean | null): string | null => {
  if (diff === null || ahead === null) return null
  return ahead ? `Phase 2 ahead by +${diff.toFixed(2)}R` : `Phase 2 behind by ${Math.abs(diff).toFixed(2)}R`
}

const SideCard = ({
  title,
  side,
  leading,
  testId,
  showSplit,
}: {
  title: string
  side: Phase2TrialSide
  leading: boolean
  testId: string
  showSplit?: boolean
}) => (
  <div
    className={`rounded-md border p-3 ${leading ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}
    data-testid={testId}
    data-leading={leading ? 'true' : 'false'}
  >
    <p className="text-xs font-medium text-gray-600">{title}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{signedR(side.avg_gross_r)}</p>
    <p className="text-xs text-gray-500">avg gross R</p>
    <p className="mt-2 text-sm tabular-nums text-gray-700">
      {side.n} closed · {side.wins}W / {side.losses}L
    </p>
    {showSplit && (
      <p className="mt-1 text-xs tabular-nums text-gray-600" data-testid={`${testId}-split`}>
        Longs {side.longs.n} ({signedR(side.longs.avg_gross_r)}) · Shorts {side.shorts.n} (
        {signedR(side.shorts.avg_gross_r)})
      </p>
    )}
    {side.bets && (
      <p className="mt-1 text-xs tabular-nums text-gray-600" data-testid={`${testId}-bets`}>
        {side.bets.n} independent bet{side.bets.n === 1 ? '' : 's'} ({signedR(side.bets.avg_gross_r)} avg per bet)
      </p>
    )}
  </div>
)

const Progress = ({ n, decisionAt, headsUp, start }: { n: number; decisionAt: number; headsUp: number[]; start: string | null }) => {
  const pct = Math.min(n / decisionAt, 1) * 100
  return (
    <div data-testid="trial-progress">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-gray-800">
          {Math.min(n, decisionAt)} of {decisionAt} Phase 2 setups closed
        </span>
        <span className="text-xs text-gray-500">Started {day(start)}</span>
      </div>
      <div
        className="relative mt-2 h-2 rounded-full bg-gray-200"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={decisionAt}
        aria-valuenow={Math.min(n, decisionAt)}
      >
        <div className="h-2 rounded-full bg-sky-500" style={{ width: `${pct}%` }} />
        {[...headsUp, decisionAt].map((m) => (
          <span
            key={m}
            className={`absolute top-[-3px] h-3.5 w-0.5 ${m === decisionAt ? 'bg-gray-800' : 'bg-gray-400'}`}
            style={{ left: `calc(${(m / decisionAt) * 100}% - 1px)` }}
            data-testid={`trial-marker-${m}`}
            title={m === decisionAt ? `${m}: decision` : `${m}: heads-up`}
          />
        ))}
      </div>
      <div className="relative mt-1 h-4 text-[10px] text-gray-500">
        {[...headsUp, decisionAt].map((m) => (
          <span key={m} className="absolute -translate-x-1/2" style={{ left: `${(m / decisionAt) * 100}%` }}>
            {m === decisionAt ? `${m} decision` : m}
          </span>
        ))}
      </div>
    </div>
  )
}

const Phase2TrialScoreboard = () => {
  const [data, setData] = useState<Phase2Trial | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    Promise.resolve()
      .then(() => adminAPI.phase2Trial())
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiErrorMessage(e, 'Could not load the Phase 2 trial')))
    return () => {
      live = false
    }
  }, [])

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3" data-testid="phase2-trial">
      <h3 className="text-base font-semibold text-gray-900">Phase 2 trial</h3>
      <p className="text-xs text-gray-500" data-testid="trial-subtitle">
        {TRIAL_SUBTITLE}
      </p>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!data && !error && <p className="mt-2 text-sm text-gray-500">Loading…</p>}

      {data && data.status === 'waiting' && (
        <p className="mt-2 text-sm text-gray-600" data-testid="trial-waiting">
          {WAITING_TEXT}
        </p>
      )}

      {data && data.status !== 'waiting' && data.phase1 && data.phase2 && (
        <div className="mt-3 space-y-3 text-sm">
          <Progress n={data.phase2.n} decisionAt={data.decision_at} headsUp={data.heads_up} start={data.start} />

          {data.frozen && (
            <div className="rounded-md border border-gray-300 bg-white p-3" data-testid="trial-frozen">
              <p className="font-medium text-gray-900">
                Result at {data.decision_at} (frozen {day(data.frozen.fired_at)}): {data.frozen.summary}
              </p>
              <p className="mt-1 text-gray-700">
                Phase 2 {signedR(data.frozen.phase2.avg_gross_r)} over {data.frozen.phase2.n} vs Phase 1{' '}
                {signedR(data.frozen.phase1.avg_gross_r)} over {data.frozen.phase1.n}. No automatic switch:{' '}
                {data.mode === 'shadow' ? 'Phase 1 still decides.' : 'Phase 2 decides (set by hand).'}
              </p>
            </div>
          )}

          {data.frozen && <p className="text-xs font-medium text-gray-500">Since then, running</p>}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SideCard
              title={data.labels.phase1}
              side={data.phase1}
              leading={data.ahead === false}
              testId="trial-phase1"
              showSplit
            />
            <SideCard
              title={data.labels.phase2}
              side={data.phase2}
              leading={data.ahead === true}
              testId="trial-phase2"
              showSplit
            />
          </div>

          <p className="text-gray-800" data-testid="trial-verdict">
            <strong>{verdictText(data.diff, data.ahead) ?? 'Nothing to compare yet.'}</strong>
            {!data.frozen && <> {NO_SWITCH_TEXT}</>}
          </p>

          {disagreementText(data.disagreements) && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-gray-800" data-testid="trial-disagreements">
              <strong>Where they disagree:</strong> {disagreementText(data.disagreements)}
              {data.bet_window_hours && (
                <span className="block text-xs text-gray-500">
                  A bet is same-direction setups opened within {data.bet_window_hours}h of each other: coins move together.
                </span>
              )}
            </p>
          )}

          <p className="rounded-md bg-white px-3 py-2 text-gray-700" data-testid="trial-rule">
            Agreed {data.rule.agreed}: {data.rule.text}
          </p>

          {data.recent.length > 0 && (
            <table className="w-full text-xs" data-testid="trial-recent">
              <thead>
                <tr className="text-gray-500">
                  <th className="px-1 py-1 text-left font-medium">Closed</th>
                  <th className="px-1 py-1 text-left font-medium">Setup</th>
                  <th className="px-1 py-1 text-right font-medium">Result</th>
                  <th className="px-1 py-1 text-left font-medium">Phase 1</th>
                  <th className="px-1 py-1 text-left font-medium">Phase 2</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={`${r.source}-${r.id}`} className="border-t border-gray-100" data-testid="trial-recent-row">
                    <td className="px-1 py-1 text-gray-600">{day(r.closed_at)}</td>
                    <td className="px-1 py-1 text-gray-800">
                      {r.symbol} {r.direction} {r.timeframe}
                    </td>
                    <td className={`px-1 py-1 text-right tabular-nums ${r.gross_r >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {signedR(r.gross_r)}
                    </td>
                    <td className="px-1 py-1 text-gray-700">{r.phase1}</td>
                    <td className="px-1 py-1 text-gray-700">{r.phase2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <p className="text-xs text-gray-400" data-testid="trial-caveat">
            {data.caveat}
          </p>
        </div>
      )}
    </div>
  )
}

export default Phase2TrialScoreboard
